import React from "react";
import ReactDOM from "react-dom";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import moment from "moment";
import "./ScoreboardPDFReport.css";
import vozoLogo from "../../../assets/images/vozo-logo-1.png";

/**
 * ScoreboardReportComponent - The visual template for the PDF
 */
const ScoreboardReportComponent = ({ rowData, patientInfo, facilityInfo }) => {
    const currentDate = moment().format("DD/MM/YYYY");

    // Logic from Scoreboard.js to calculate scores and segments
    const currentScore = rowData?.Assessment_values?.reduce((sum, item) => sum + (item?.score || 0), 0) || 0;

    const getDefaultColors = (count) => {
        if (count === 3) return ["#27AE60", "#F2C94C", "#EB5757"];
        if (count === 4) return ["#56CCF2", "#27AE60", "#F2994A", "#EB5757"];
        if (count === 5) return ["#56CCF2", "#27AE60", "#F2C94C", "#F2994A", "#EB5757"];
        return ["#56CCF2", "#27AE60", "#F2C94C", "#F2994A", "#EB5757"];
    };

    const dynamicGuide = (rowData?.scoring_guide || []).map((item, index) => {
        const colors = getDefaultColors(rowData.scoring_guide.length);
        return {
            min: item.min,
            max: item.max,
            severity: item.label,
            score: `${item.min}-${item.max}`,
            color: colors[index] || "#999"
        };
    });

    const maxScore = dynamicGuide.length ? Math.max(...dynamicGuide.map(g => g.max)) : 21;
    const severityLevel = dynamicGuide.find(g => currentScore >= g.min && currentScore <= g.max) || dynamicGuide[0];
    const activeIndex = dynamicGuide.findIndex(g => currentScore >= g.min && currentScore <= g.max);

    const scoringData = (rowData?.Assessment_question || []).map((q, index) => {
        const selected = rowData?.Assessment_values?.find(v => v.question_id === q.question_id);
        const selectedOption = rowData?.Assessment_options?.find(o => o.option_id === selected?.selected_option_id);
        return {
            sno: index + 1,
            question: q.question,
            response: selectedOption?.option || "-",
            score: selected ? `${selected.score}/3` : "- / 3"
        };
    });

    // Gauge calculation helpers
    const radius = 80;
    const center = 100;
    const polarToCartesian = (cx, cy, r, angle) => {
        const rad = (angle - 180) * (Math.PI / 180);
        return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    };

    const describeArc = (startAngle, endAngle, color) => {
        const strokeWidth = 12;
        const start = polarToCartesian(center, center, radius, endAngle);
        const end = polarToCartesian(center, center, radius, startAngle);
        return (
            <path
                d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 0 0 ${end.x} ${end.y}`}
                stroke={color}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
            />
        );
    };

    const GAP = 14;
    const TOTAL_ARC = 180;
    const SEGMENT_COUNT = dynamicGuide.length || 1;
    const segmentAngle = (TOTAL_ARC - GAP * (SEGMENT_COUNT - 1)) / SEGMENT_COUNT;

    return (
        <div className="scoreboard-pdf-export-container" id="scoreboard-report-template">
            <div className="pdf-report-header">
                <div className="pdf-report-logo">
                    <img src={vozoLogo} alt="VOZO" className="pdf-logo-img" />
                </div>
                <div className="pdf-report-date">Date : {currentDate}</div>
            </div>

            <div className="pdf-patient-provider-details">
                <div className="pdf-detail-section">
                    <div className="pdf-detail-item">
                        <span className="pdf-label-bold">Patient Name:</span>
                        <span className="pdf-value-text">{patientInfo?.fname} {patientInfo?.lname}</span>
                    </div>
                    <div className="pdf-detail-item">
                        <span className="pdf-label-bold">Date of Birth:</span>
                        <span className="pdf-value-text">{patientInfo?.dob ? moment(patientInfo.dob).format("DD/MM/YYYY") : "-"}</span>
                    </div>
                </div>
                <div className="pdf-detail-section pdf-right-block">
                    <div className="pdf-detail-item">
                        <span className="pdf-label-bold">Provider Name:</span>
                        <span className="pdf-value-text">{patientInfo?.provider_name || facilityInfo?.provider_name || "-"}</span>
                    </div>
                    <div className="pdf-facility-info">
                        <div>{facilityInfo?.name || "-"}</div>
                        <div>{facilityInfo?.street || "-"}, {facilityInfo?.city || "-"}, {facilityInfo?.state || "-"} {facilityInfo?.zip || ""}</div>
                        <div>PH: {facilityInfo?.phone || "-"}</div>
                    </div>
                </div>
            </div>

            <div className="pdf-horizontal-divider"></div>

            <h2 className="pdf-assessment-title">{rowData?.form_name?.includes("Scoring Guide") ? rowData.form_name : `${rowData?.form_name || "Assessment"} Scoring Guide`}</h2>

            <div className="pdf-score-summary-card">
                <div className="pdf-gauge-inner-card">
                    <div className="pdf-gauge-label-top">Score</div>
                    <div className="pdf-gauge-svg-wrapper">
                        <svg width="250" height="140" viewBox="0 0 200 120">
                            {dynamicGuide.map((item, i) => {
                                const start = i * (segmentAngle + GAP);
                                const end = start + segmentAngle;
                                const strokeColor = i <= activeIndex ? item.color : "#E0E0E0";
                                return <React.Fragment key={i}>{describeArc(start, end, strokeColor)}</React.Fragment>;
                            })}
                            {Array.from({ length: maxScore + 1 }).map((_, i) => {
                                const angle = 5 + ((175 - 5) / maxScore) * i;
                                const tickRadius = radius - 15;
                                const { x, y } = polarToCartesian(center, center, tickRadius, angle);
                                return <circle key={i} cx={x} cy={y} r="1" fill="#BDBDBD" />;
                            })}
                            <text x="10" y="115" className="pdf-gauge-tick-text">0</text>
                            <text x="180" y="115" className="pdf-gauge-tick-text">{maxScore}</text>
                        </svg>
                    </div>
                    <div className="pdf-gauge-value-overlay">
                        <div className="pdf-value-big">{currentScore}</div>
                        <div className="pdf-value-label">{severityLevel?.severity}</div>
                    </div>
                </div>

                <div className="pdf-guide-column">
                    <div className="pdf-guide-header-title">{rowData?.form_name?.includes("Scoring") ? rowData.form_name : `${rowData?.form_name || "Assessment"} Scoring Guide`}</div>
                    <table className="pdf-guide-data-table">
                        <thead>
                            <tr>
                                <th style={{ color: "#2F80ED" }}>Score</th>
                                <th style={{ color: "#2F80ED" }}>Anxiety Severity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dynamicGuide.map((item, idx) => (
                                <tr key={idx}>
                                    <td>
                                        <span className="pdf-color-dot" style={{ backgroundColor: item.color }} />
                                        {item.score}
                                    </td>
                                    <td>{item.severity}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="pdf-responses-table-wrapper">
                <table className="pdf-responses-table">
                    <thead>
                        <tr>
                            <th style={{ width: "7%", textAlign: "center" }}>S.No</th>
                            <th style={{ width: "53%" }}>Questions</th>
                            <th style={{ width: "28%" }}>Response</th>
                            <th style={{ width: "12%", textAlign: "center" }}>Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scoringData.map((row, idx) => (
                            <tr key={idx}>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>{row.sno}</td>
                                <td style={{ verticalAlign: "middle" }}>{row.question}</td>
                                <td style={{ verticalAlign: "middle" }}>{row.response}</td>
                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>{row.score}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pdf-report-footer">
                Powered by VOZO Documentation
            </div>
        </div>
    );
};

/**
 * downloadScoreboardPDF - The main export function to trigger the PDF download
 * It captures a temporary hidden instance of the component as PNG and puts it in a PDF
 */
export const downloadScoreboardPDF = async (rowData, patientInfo, facilityInfo) => {
    // 1. Create a temporary container for rendering the component
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.top = "-9999px";
    container.style.left = "-9999px";
    container.style.width = "800px"; // Standard width for capture
    document.body.appendChild(container);

    try {
        // 2. Render the component into the temporary container
        ReactDOM.render(
            <ScoreboardReportComponent
                rowData={rowData}
                patientInfo={patientInfo}
                facilityInfo={facilityInfo}
            />,
            container
        );

        // 3. Wait a moment for rendering to complete (especially images/fonts)
        await new Promise(resolve => setTimeout(resolve, 500));

        // 4. Capture as PNG using html2canvas
        const element = container.querySelector("#scoreboard-report-template");
        const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            logging: false
        });

        // 5. Convert to PDF using jsPDF
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4"
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10; // 10mm margin
        const maxLineWidth = pageWidth - (margin * 2);
        const maxLineHeight = pageHeight - (margin * 2);

        let imgWidth = maxLineWidth;
        let imgHeight = (canvas.height * imgWidth) / canvas.width;

        // If the image is taller than the page, scale it down to fit vertically
        if (imgHeight > maxLineHeight) {
            const ratio = maxLineHeight / imgHeight;
            imgHeight = maxLineHeight;
            imgWidth = imgWidth * ratio;
        }

        // Center horizontally
        const xPos = (pageWidth - imgWidth) / 2;
        // Center vertically or start from margin
        // const yPos = imgHeight < maxLineHeight ? (pageHeight - imgHeight) / 2 : margin;
const yPos=margin;
        pdf.addImage(imgData, "PNG", xPos, yPos, imgWidth, imgHeight);

        // 6. Save the PDF
        const fileName = `${rowData?.form_name || "Scoreboard"}_${moment().format("YYYY-MM-DD")}.pdf`;
        pdf.save(fileName);

    } catch (error) {
        console.error("Error generating Scoreboard PDF:", error);
    } finally {
        // 7. Cleanup
        ReactDOM.unmountComponentAtNode(container);
        document.body.removeChild(container);
    }
};
